import { useState, useEffect } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('contact-section');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitMessage('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    if (formData.message.length > 500) {
      setSubmitMessage('Die Nachricht darf maximal 500 Zeichen enthalten.');
      return;
    }

    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d6s63qsddmmni7ck9830', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns bald bei Ihnen.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
        });
      } else {
        setSubmitMessage('Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      setSubmitMessage('Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 8000);
    }
  };

  return (
    <section id="contact-section" className="relative min-h-screen flex items-center justify-center bg-black py-20 md:py-32 overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-black to-black"></div>
      
      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#d1bc8a]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#d1bc8a]/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Header */}
        <div className={`text-center mb-12 md:mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <h2 className="text-4xl md:text-7xl lg:text-8xl font-bold text-white mb-6 md:mb-8 tracking-tight">
            Bereit für Ihr Projekt?
          </h2>
          <p className="text-lg md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Lassen Sie uns gemeinsam etwas Großartiges schaffen
          </p>
          
          {/* Email Link */}
          <a 
            href="mailto:bamme.media@gmail.com"
            className="inline-block mt-8 md:mt-12 text-xl md:text-3xl font-semibold text-[#d1bc8a] hover:text-white transition-all duration-500 group"
          >
            bamme.media@gmail.com
            <div className="h-0.5 bg-[#d1bc8a] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
          </a>
        </div>

        {/* Submit Message */}
        {submitMessage && (
          <div className={`mb-6 md:mb-8 p-4 md:p-6 rounded-2xl text-center max-w-2xl mx-auto ${
            submitMessage.includes('erfolgreich') 
              ? 'bg-green-500/10 text-green-400 border border-green-500/30' 
              : 'bg-red-500/10 text-red-400 border border-red-500/30'
          }`}>
            {submitMessage}
          </div>
        )}

        {/* Contact Form */}
        <div className={`max-w-4xl mx-auto transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <form onSubmit={handleSubmit} className="space-y-5 md:space-y-8" data-readdy-form id="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
              {/* Name Input */}
              <div className="group">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ihr Name"
                  required
                  className="w-full px-5 py-4 md:px-8 md:py-6 bg-transparent border-2 border-white/20 rounded-2xl text-white text-base md:text-lg placeholder-gray-500 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500"
                />
              </div>

              {/* Email Input */}
              <div className="group">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Ihre E-Mail"
                  required
                  className="w-full px-5 py-4 md:px-8 md:py-6 bg-transparent border-2 border-white/20 rounded-2xl text-white text-base md:text-lg placeholder-gray-500 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500"
                />
              </div>

              {/* Company Input */}
              <div className="group">
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Ihr Unternehmen (optional)"
                  className="w-full px-5 py-4 md:px-8 md:py-6 bg-transparent border-2 border-white/20 rounded-2xl text-white text-base md:text-lg placeholder-gray-500 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500"
                />
              </div>

              {/* Phone Input */}
              <div className="group">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Ihre Telefonnummer (optional)"
                  className="w-full px-5 py-4 md:px-8 md:py-6 bg-transparent border-2 border-white/20 rounded-2xl text-white text-base md:text-lg placeholder-gray-500 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500"
                />
              </div>
            </div>

            {/* Message Textarea */}
            <div className="group">
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Erzählen Sie uns von Ihrem Projekt..."
                rows={8}
                maxLength={500}
                required
                className="w-full px-5 py-4 md:px-8 md:py-6 bg-transparent border-2 border-white/20 rounded-2xl text-white text-base md:text-lg placeholder-gray-500 focus:outline-none focus:border-[#d1bc8a] focus:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500 resize-none"
              />
              <div className="text-right text-sm text-gray-500 mt-2">
                {formData.message.length}/500
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative inline-flex items-center justify-center w-full md:w-auto px-10 md:px-16 py-5 md:py-6 text-lg md:text-xl font-semibold text-white border-2 border-white/30 rounded-full overflow-hidden transition-all duration-500 hover:border-[#d1bc8a] hover:shadow-[0_0_40px_rgba(209,188,138,0.4)] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <span className="absolute inset-0 bg-[#d1bc8a] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></span>
                <span className="relative z-10 flex items-center space-x-3">
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                      <span>Wird gesendet...</span>
                    </>
                  ) : (
                    <>
                      <span>Nachricht senden</span>
                      <i className="ri-arrow-right-line text-2xl"></i>
                    </>
                  )}
                </span>
              </button>
            </div>
          </form>
        </div>

        {/* Contact Info */}
        <div className={`mt-20 md:mt-32 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-5xl mx-auto transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Phone */}
          <a href="tel:017645725863" className="group text-center">
            <div className="w-20 h-20 mx-auto mb-6 border-2 border-white/20 rounded-full flex items-center justify-center group-hover:border-[#d1bc8a] group-hover:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500">
              <i className="ri-phone-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-semibold text-white mb-2">Telefon</h4>
            <p className="text-gray-400 group-hover:text-[#d1bc8a] transition-colors duration-300">017645725863</p>
          </a>

          {/* Email */}
          <a href="mailto:bamme.media@gmail.com" className="group text-center">
            <div className="w-20 h-20 mx-auto mb-6 border-2 border-white/20 rounded-full flex items-center justify-center group-hover:border-[#d1bc8a] group-hover:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500">
              <i className="ri-mail-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-semibold text-white mb-2">E-Mail</h4>
            <p className="text-gray-400 group-hover:text-[#d1bc8a] transition-colors duration-300">bamme.media@gmail.com</p>
          </a>

          {/* Location */}
          <div className="group text-center">
            <div className="w-20 h-20 mx-auto mb-6 border-2 border-white/20 rounded-full flex items-center justify-center group-hover:border-[#d1bc8a] group-hover:shadow-[0_0_30px_rgba(209,188,138,0.3)] transition-all duration-500">
              <i className="ri-map-pin-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-semibold text-white mb-2">Standort</h4>
            <p className="text-gray-400">38855 Wernigerode<br />Deutschland</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;